// src/components/TimelineTab.jsx
import React, { useState, useEffect, useCallback } from 'react';
import './TimelineTab.css';
import { apiFetch } from '../lib/api';

const FILTERS = [
  { key: 'all',         label: 'All',          icon: '🕐' },
  { key: 'signal',      label: 'Signals',       icon: '📡' },
  { key: 'opportunity', label: 'Opportunities', icon: '💼' },
  { key: 'contact',     label: 'Contacts',      icon: '👤' },
  { key: 'activity',    label: 'Activities',    icon: '📋' },
];

function formatDate(dateStr) {
  if (!dateStr) return '';
  // Append T12:00:00 to plain date strings (YYYY-MM-DD) to prevent UTC midnight
  // from rolling the date back one day in US Eastern time
  const safe = (typeof dateStr === 'string' && dateStr.length === 10) ? dateStr + 'T12:00:00' : dateStr;
  const d = new Date(safe);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatRelative(dateStr) {
  if (!dateStr) return '';
  const now = new Date();
  const d = new Date(dateStr);
  // Compare calendar dates in local time to avoid UTC boundary issues
  const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dDate   = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round((nowDate - dDate) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
  return `${Math.floor(diffDays / 365)}y ago`;
}

function getSourceLabel(source) {
  const map = {
    signal: 'Signal',
    opportunity: 'Opportunity',
    contact: 'Contact Added',
    activity: 'Activity',
  };
  return map[source] || source;
}

export default function TimelineTab({ accountId, accountName }) {
  const [events, setEvents] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);

  const fetchTimeline = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/timeline/by-account?accountId=${accountId}`);
      const data = await res.json();
      if (data.success) {
        setEvents(data.events);
        setCounts(data.counts);
      } else {
        setError(data.error || 'Failed to load timeline');
      }
    } catch (err) {
      setError('Could not connect to server');
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    fetchTimeline();
  }, [fetchTimeline]);

  const filtered = activeFilter === 'all'
    ? events
    : events.filter(e => e.source === activeFilter);

  // Group events by month/year for section headers
  function getMonthYear(dateStr) {
    if (!dateStr) return 'Unknown';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }

  const grouped = [];
  let lastGroup = null;
  filtered.forEach(event => {
    const group = getMonthYear(event.date);
    if (group !== lastGroup) {
      grouped.push({ type: 'header', label: group });
      lastGroup = group;
    }
    grouped.push({ type: 'event', event });
  });

  // Empty state: if no events at all, show signals-only message
  const isEmpty = events.length === 0;
  const isFilterEmpty = filtered.length === 0 && events.length > 0;

  if (loading) {
    return (
      <div className="tl-container">
        <div className="tl-loading">
          <div className="tl-spinner" />
          <p>Loading timeline...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tl-container">
        <div className="tl-error">
          <span>⚠️</span>
          <p>{error}</p>
          <button className="tl-retry" onClick={fetchTimeline}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="tl-container">

      {/* Header */}
      <div className="tl-header">
        <div className="tl-header-left">
          <h3 className="tl-title">Account Timeline</h3>
          {counts.total > 0 && (
            <span className="tl-total-badge">{counts.total} events</span>
          )}
        </div>
        <div className="tl-source-pills">
          {counts.signals > 0 && (
            <span className="tl-source-pill tl-pill-signal">📡 {counts.signals} Signals</span>
          )}
          {counts.opportunities > 0 && (
            <span className="tl-source-pill tl-pill-opportunity">💼 {counts.opportunities} Opportunities</span>
          )}
          {counts.contacts > 0 && (
            <span className="tl-source-pill tl-pill-contact">👤 {counts.contacts} Contacts</span>
          )}
          {counts.activities > 0 && (
            <span className="tl-source-pill tl-pill-activity">📋 {counts.activities} Activities</span>
          )}
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="tl-filters">
        {FILTERS.map(f => {
          const count = f.key === 'all' ? counts.total : counts[f.key + 's'] ?? counts[f.key];
          return (
            <button
              key={f.key}
              className={`tl-filter-btn ${activeFilter === f.key ? 'tl-filter-active' : ''}`}
              onClick={() => setActiveFilter(f.key)}
            >
              {f.icon} {f.label}
              {count > 0 && <span className="tl-filter-count">{count}</span>}
            </button>
          );
        })}
      </div>

      {/* Empty States */}
      {isEmpty && (
        <div className="tl-empty">
          <div className="tl-empty-icon">🕐</div>
          <h4>No timeline history yet</h4>
          <p>Add strategic signals to start building the account timeline for {accountName}.</p>
        </div>
      )}

      {isFilterEmpty && (
        <div className="tl-empty">
          <div className="tl-empty-icon">🔍</div>
          <h4>No {activeFilter} events found</h4>
          <p>Try switching to a different filter or view all events.</p>
          <button className="tl-retry" onClick={() => setActiveFilter('all')}>Show All Events</button>
        </div>
      )}

      {/* Timeline Feed */}
      {!isEmpty && !isFilterEmpty && (
        <div className="tl-feed">
          <div className="tl-line" />
          {grouped.map((item, i) => {
            if (item.type === 'header') {
              return (
                <div key={`header-${i}`} className="tl-month-header">
                  <span>{item.label}</span>
                </div>
              );
            }
            const e = item.event;
            const isExpanded = expandedId === e.id;
            return (
              <div
                key={e.id}
                className={`tl-event ${isExpanded ? 'tl-event-expanded' : ''}`}
                onClick={() => setExpandedId(isExpanded ? null : e.id)}
              >
                {/* Dot on timeline */}
                <div className="tl-dot" style={{ background: e.color, borderColor: e.color }}>
                  <span className="tl-dot-icon">{e.icon}</span>
                </div>

                {/* Card */}
                <div className="tl-card" style={{ borderLeftColor: e.color }}>
                  <div className="tl-card-top">
                    <div className="tl-card-left">
                      <span className="tl-source-tag" style={{ background: e.bg, color: e.color }}>
                        {getSourceLabel(e.source)}
                        {e.subtitle && ` · ${e.subtitle}`}
                      </span>
                      <h4 className="tl-event-title">{e.title}</h4>
                    </div>
                    <div className="tl-card-right">
                      <span className="tl-date-rel">{formatRelative(e.date)}</span>
                      <span className="tl-date-full">{formatDate(e.date)}</span>
                    </div>
                  </div>

                  {/* Badges row */}
                  <div className="tl-card-meta">
                    {e.badge && (
                      <span className="tl-badge" style={{ background: e.bg, color: e.color }}>{e.badge}</span>
                    )}
                    {e.meta && (
                      <span className="tl-meta-text">{e.meta}</span>
                    )}
                    {e.detail && !isExpanded && (
                      <span className="tl-preview">{e.detail.slice(0, 80)}{e.detail.length > 80 ? '...' : ''}</span>
                    )}
                    <span className="tl-expand-hint">{isExpanded ? '▲ less' : e.detail ? '▼ more' : ''}</span>
                  </div>

                  {/* Expanded detail */}
                  {isExpanded && e.detail && (
                    <div className="tl-detail-block">
                      <p className="tl-detail-text">{e.detail}</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
